<template>
  <e-dialog
    v-if="isActiveStatus && !isOperationHidden(accommodationObject?.operations?.canAddContract)"
    v-model="isOpened"
    @after-leave="clearData"
  >
    <template #activator>
      <slot name="activator" />
    </template>
    <template #title>
      <div class="caption">Новый аккаунт</div>
    </template>

    <e-form v-model="isValid" ref="formRef" class="create-account" @submit="submit">
      <e-select
        v-model="data.legal"
        :items="shownLegals"
        :rules="[required()]"
        item-title="name[0].value"
        item-value="cid"
        label="Юридическое лицо"
        class="mb-4"
      />
      <div class="d-flex mb-4 align-start">
        <e-select
          model-value="Оплата туроператором"
          :items="['Оплата туроператором']"
          label="Способ расчёта"
          disabled
        />
        <e-select
          v-model="data.currency"
          :items="currency"
          :rules="[required()]"
          item-title="name"
          item-value="currencyISOCode"
          label="Валюта расчёта"
          class="create-account__currency-select"
        />
      </div>
      <legal-origin-select v-model="data.legalOrigin" class="mb-6" label="Cтрана резидентства юридического лица" />

      <template v-if="data.legalOrigin === LegalOrigin.Ru">
        <e-alert v-if="isOfferAcceptedRequired" thin variant="error">
          <b class="paragraph-m_h"> Необходимо принять договор оферты </b>
        </e-alert>
        <e-alert v-if="isDownloadOfferContractError && !data.legal" class="mt-4" thin variant="error">
          <b> Необходимо выбрать юридическое лицо </b>
        </e-alert>

        <div class="paragraph-m_h mt-4">
          Выберите один из&nbsp;договоров оферты, по&nbsp;условиям которого желаете сотрудничать с&nbsp;нами:
        </div>

        <div class="paragraph-m_h mt-4 font-600">
          Договор оферты
          <span class="link" @click="openOfferContract(ContractType.Agency)"
            >агентский</span
          >
          <e-loader
            v-if="isDownloadOfferContractLoading === ContractType.Agency"
            :size="16"
            :width="2"
            class="text-link ml-1"
          />
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Тарифные планы заводятся как <b>«Брутто тариф»</b>. Применяется схема для взаиморасчётов&nbsp;&mdash;&nbsp;80%
          от цены переданной в EXTRANET плюс агентское вознаграждение 50&nbsp;руб., предоставляемое за реализацию
          Агентом услуг. Цена продажи равна <b>&laquo;Брутто тарифу&raquo;</b>
        </div>

        <e-checkbox
          v-model="data.isAgencyAccepted"
          label="Ознакомлен и принимаю условия договора оферты"
          class="mt-2"
          no-ellipsis
          @update:model-value="handleAgencyAcceptedChange"
        />

        <divider class="ma-4 w-auto" />

        <div class="paragraph-m_h mt-4 font-600">
          Договор оферты
          <span class="link" @click="openOfferContract(ContractType.Sale)"
            >купля-продажа &laquo;Нетто тариф&raquo;</span
          >
          <e-loader
            v-if="isDownloadOfferContractLoading === ContractType.Sale"
            :size="16"
            :width="2"
            class="text-link ml-1"
          />
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Тарифные планы заводятся как <b>«Нетто тариф»</b>. Применяется схема для взаиморасчётов по&nbsp;номинальному
          тарифу. Цену продажи регулирует Алеан.
        </div>

        <e-checkbox
          v-model="data.isBuySellAccepted"
          label="Ознакомлен и принимаю условия договора оферты"
          class="mt-2"
          no-ellipsis
          @update:model-value="handleBuySellAcceptedChange"
        />

        <divider class="ma-4 w-auto" />

        <div class="paragraph-m_h mt-4 font-600">
          Договор оферты
          <span class="link" @click="openOfferContract(ContractType.SaleGross)"
            >купля-продажа &laquo;Брутто тариф&raquo;</span
          >
          <e-loader
            v-if="isDownloadOfferContractLoading === ContractType.SaleGross"
            :size="16"
            :width="2"
            class="text-link ml-1"
          />
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Применяется схема для взаиморасчётов&nbsp;-&nbsp;<b>«Брутто тариф»</b> минус скидка 20&nbsp;% от цены, переданной в EXTRANET. Цена на поиске равна <b>«Брутто тарифу»</b>
        </div>

        <e-checkbox
          v-model="data.isSaleGrossAccepted"
          label="Ознакомлен и принимаю условия договора оферты"
          class="mt-2"
          no-ellipsis
          @update:model-value="handleSaleGrossAcceptedChange"
        />
      </template>

      <template v-else>
        <e-alert v-if="isOfferAcceptedRequired" thin variant="error">
          <b class="paragraph-m_h"> Необходимо принять договор </b>
        </e-alert>
        <e-alert v-if="isDownloadOfferContractError && !data.legal" class="mt-4" thin variant="error">
          <b> Необходимо выбрать юридическое лицо </b>
        </e-alert>

        <div class="paragraph-m_h mt-4">
          Выберите вид договора, по&nbsp;условиям которого желаете сотрудничать с&nbsp;нами:
        </div>

        <div class="paragraph-m_h mt-4 font-600 d-flex align-center">
          <e-checkbox
            v-model="data.isAgencyAccepted"
            rounded
            class="mr-1"
            @update:model-value="handleAgencyAcceptedChange"
          />
          Договор агентский
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Тарифные планы заводятся как <b>«Брутто тариф»</b>. Применяется схема для взаиморасчётов&nbsp;&mdash;&nbsp;80%
          от цены переданной в EXTRANET плюс агентское вознаграждение 50&nbsp;руб., предоставляемое за реализацию
          Агентом услуг. Цена продажи равна <b>&laquo;Брутто тарифу&raquo;</b>
        </div>

        <divider class="ma-4 w-auto" />

        <div class="paragraph-m_h mt-4 font-600 d-flex align-center">
          <e-checkbox
            v-model="data.isBuySellAccepted"
            rounded
            class="mr-1"
            @update:model-value="handleBuySellAcceptedChange"
          />
          Договор купля-продажа &laquo;Нетто тариф&raquo;
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Тарифные планы заводятся как <b>«Нетто тариф»</b>. Применяется схема для взаиморасчётов по&nbsp;номинальному
          тарифу. Цену продажи регулирует Алеан.
        </div>

        <divider class="ma-4 w-auto" />

        <div class="paragraph-m_h mt-4 font-600 d-flex align-center">
          <e-checkbox
            v-model="data.isSaleGrossAccepted"
            rounded
            class="mr-1"
            @update:model-value="handleSaleGrossAcceptedChange"
          />
          Договор купля-продажа &laquo;Брутто тариф&raquo;
        </div>

        <div class="paragraph-s_h mt-1 text-secondary">
          Применяется схема для взаиморасчётов&nbsp;-&nbsp;<b>«Брутто тариф»</b> минус скидка 20&nbsp;% от цены, переданной в EXTRANET. Цена на поиске равна <b>«Брутто тарифу»</b>
        </div>
      </template>

      <div class="d-flex mt-6 justify-end">
        <e-btn variant="secondary" class="mr-4" @click="close"> Отмена</e-btn>
        <e-btn :loading="isLoading" variant="primary" type="submit"> Создать</e-btn>
      </div>
    </e-form>
  </e-dialog>
</template>

<script setup lang="ts">
import { onMounted, ref, toRef } from 'vue';
import { useRouter } from 'vue-router';
import LegalOriginSelect from '@/core/components/common/LegalOriginSelect.vue';
import Divider from '@/core/components/helpers/Divider.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EAlert, EBtn, ECheckbox, EForm, ELoader, ESelect } from '@/core/components/ui';
import { useSuggestions } from '@/core/composables/suggestions';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { useTourStore } from '@/core/store/tour';
import { openDocument } from '@/core/utils/file';
import { isOperationHidden } from '@/core/utils/permissions';
import { required } from '@/core/utils/validation';
import { ApproveStatus, ContractType, type CurrencyListQuery, LegalOrigin } from '@/graphql/generated';
import { useLegalsStore } from '@/parts/legals/store/legals';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useOfferContractsStore } from '@/parts/objects/store/offerContracts';

defineSlots<{
  activator: () => void;
}>();

const $router = useRouter();

const { getLegals, legals } = useLegalsStore();

const defaultData = () => ({
  legal: null,
  currency: null,
  legalOrigin: LegalOrigin.Ru,
  isAgencyAccepted: false,
  isBuySellAccepted: false,
  isSaleGrossAccepted: false
});

const isOfferAcceptedRequired = toRef(
  () => !data.value.isAgencyAccepted && !data.value.isBuySellAccepted && !data.value.isSaleGrossAccepted && formRef.value?.isDirty,
);

const shownLegals = toRef(
  () => legals.value?.legals.data?.filter((l) => l.approveStatus === ApproveStatus.Approved) || [],
);

const data = ref(defaultData());

const clearData = () => {
  data.value = defaultData();
};

const { setActiveTour } = useTourStore();
const { getCurrencyList } = useSuggestions();
const { createAccount, isActiveStatus, accommodationObject } = useAccommodationObjectsStore();
const { getPreviewOfferContract } = useOfferContractsStore();

const isDownloadOfferContractLoading = ref<string>();
const isDownloadOfferContractError = ref(false);

const openOfferContract = async (type: ContractType) => {
  if (!data.value.legal || !accommodationObject.value?.cid) {
    isDownloadOfferContractError.value = true;
    return;
  }

  isDownloadOfferContractError.value = false;
  isDownloadOfferContractLoading.value = type;

  const result = await getPreviewOfferContract(data.value.legal, accommodationObject.value.cid, type);

  if (result?.data) {
    openDocument(result.data.getPreviewOfferContract.data, 'Договор оферты');
  }

  isDownloadOfferContractLoading.value = undefined;
};

const handleAgencyAcceptedChange = () => {
  if (data.value.isAgencyAccepted) {
    data.value.isBuySellAccepted = false;
    data.value.isSaleGrossAccepted = false;
  }
};

const handleBuySellAcceptedChange = () => {
  if (data.value.isBuySellAccepted) {
    data.value.isAgencyAccepted = false;
    data.value.isSaleGrossAccepted = false;
  }
};

const handleSaleGrossAcceptedChange = () => {
  if (data.value.isSaleGrossAccepted) {
    data.value.isAgencyAccepted = false;
    data.value.isBuySellAccepted = false;
  }
};

const isOpened = ref(false);
const formRef = ref();

const close = () => {
  isOpened.value = false;
};

const isValid = ref(false);
const isLoading = ref(false);

const submit = async () => {
  if (!isValid.value || isOfferAcceptedRequired.value) {
    return;
  }

  isLoading.value = true;

  const result = await createAccount({
    currencyIsoCode: data.value.currency!,
    legalCid: data.value.legal!,
    legalOrigin: data.value.legalOrigin,
    contractType: data.value.isAgencyAccepted 
      ? ContractType.Agency
      : data.value.isSaleGrossAccepted
        ? ContractType.SaleGross
        : ContractType.Sale,
  });

  isLoading.value = false;

  if (result?.data) {
    setActiveTour('newAccountTour');
    close();

    await $router.push({
      name: RouteNames.ObjectInfo,
      params: { id: accommodationObject.value?.cid, acc_id: result.data.addContract.Contract?.contractNumber },
    });
  }
};

const currency = ref<CurrencyListQuery['currencyList']>([]);

onMounted(async () => {
  currency.value = await getCurrencyList();
  await getLegals();
});
</script>

<style scoped lang="scss">
.create-account {
  :deep {
    .create-account__thin-input {
      max-width: 280px;
    }
  }

  &__currency-select {
    max-width: 240px;
    margin-left: $gap-4;
  }
}
</style>
